import React, { useContext } from 'react';
import styled from 'styled-components';
import { dataStore } from 'store/data';
import { appStore } from 'store/app';
import useTotalPrice from 'hooks/useTotalPrice';
import useDiscountedPrice from 'hooks/useDiscountedPrice';
import ui from 'config-app/ui';
import { sanitize } from 'dompurify';

const Component = () => {
  const [dataState] = useContext(dataStore);
  const [appState] = useContext(appStore);
  const [totalPrice] = useTotalPrice();
  const [discountedPrice] = useDiscountedPrice();
  const labels = window.theme;
  let discountRate = 0;
  if (totalPrice > 0) {
    discountRate = (totalPrice - discountedPrice) / totalPrice;
  }
  const discountPercent = Math.round(discountRate * 100);
  const discountLabel = labels.discountFormat.replace(/{{rate}}/, discountPercent.toString());
  const sizeId = appState.customData.bottle.size;

  // 日本語のサンプルデータ
  // Details: {
  //   button01: '予約注文する',
  //   button02: 'カートに追加する',
  //   button03: '売り切れ',
  //   label01: '¥',
  //   label02: 'JPY',
  //   label03: '(税込)',
  //   text01: '<<items>>は売り切れています。<br>他のカラーまたはサイズを選択してください。'
  // }

  // 会員特典対象商品
  const { offers_for_members } = dataState;
  const offersForMembers = offers_for_members && offers_for_members.product_tags ? offers_for_members : null;
  // 対象と対象外のテキスト設定
  let offersForMembersMessage = null;
  if (offersForMembers) {
    const { product_tags, tag_name, message } = offersForMembers;
    // product_tags 配列に tag_name の値が含まれていれば、対象商品
    const isEligible = product_tags.filter((tag) => tag === tag_name).length > 0;
    offersForMembersMessage = isEligible ? message.eligible : message.not_eligible;
  }

  let hasBoot = false;
  if(appState.customData.bootsList) {
    hasBoot = true;
  }

  const renderSkuSet = () => {
    const otherSkus = [];
    appState.customData.partsList.map((item) => {
      if (item.sku) {
        otherSkus.push(item.sku);
      }
      return item;
    });
    let skuSet = `${appState.customData.bottle.sku} + ${otherSkus.join(
      ' + '
    )}`;
    if(hasBoot) {
      skuSet = skuSet + " + " + appState.customData.bootsList[0].variants[sizeId].sku;
    }
    return skuSet;
  };

  let isMember = false;

  if (discountRate > 0) {
    isMember = true;
  }

  return (
    <Container>
      <ItemInfo>
        <div className="productName">{appState.customData.bottleColorData.name} <span className="optionValues">{appState.customData.bottleColorData.variants[sizeId].size} {appState.customData.bottleColorData.variants[sizeId].color}</span></div>
        <div className="type">{renderSkuSet()}</div>
        <div className={isMember ? "pdtPrice member" : "pdtPrice"}>
          {isMember && <div className="option">{labels["regular_price"]} </div>}
          <div className="currencySymbol">{labels.price_prefix}</div><div className="totalPrice">{totalPrice.toLocaleString()}</div>
          <div className="currency">{labels.price_suffix}</div>
          <div className="tax">{labels["tax_included"]}</div>
        </div>
        {isMember && <div className="pdtPrice">
          <div className="currencySymbol">{labels.price_prefix}</div><div className="totalPrice">{discountedPrice.toLocaleString()}</div>
          <div className="currency">{labels.price_suffix}</div>
          <div className="tax">{labels["tax_included"]}</div>
          <div className="label">{discountLabel}</div>
        </div>}
        {offersForMembersMessage &&
          <div
            className='offersForMembers'
            dangerouslySetInnerHTML={{ __html: sanitize(offersForMembersMessage) }}
          />
        }
      </ItemInfo>
    </Container>
  );
};

export default Component;

const Container = styled.div`
  margin: 0;
`;

const ItemInfo = styled.div`
  .productName {
    font-weight: bold;
  }

  .type {
    color: #888888;
  }

  .pdtPrice {
    display: flex;
    flex-direction: row;
    align-items: baseline;
    div {
      display: inline-block;
    }
    &.member {
      .currencySymbol {
        font-weight: normal;
        text-decoration: line-through;
        margin-left: 5px;
      }
      .totalPrice {
        font-weight: normal;
        text-decoration: line-through;
      }
      .currency {
        font-weight: normal;
      }
    }

    .currencySymbol {
      font-weight: bold;
      letter-spacing: 0.2em;
    }

    .totalPrice {
      font-weight: bold;
    }

    .currency {
      margin-left: 0.25em;
      font-weight: bold;
    }

    .tax {
      margin-left: 0.5em;
    }

    .label {
      display: inline-block;
      background: #004EA2;
      color: #fff;
      font-weight: bold;
      margin-left: .5em;
    }
  }
  .offersForMembers {
    font-size: 0.9em;
  }
  ${ui.MQ_MD_LG} {
    .productName {
      margin-bottom: 5px;
      font-size: 30px;
    }
    .type {
      margin-bottom: 10px;
      font-size: 18px;
      margin-top: 6px;
    }
    .pdtPrice {
      font-size: 18px;
      &.member {
        margin-top: 15px;
        .currencySymbol,
        .totalPrice {
          font-size: 18px;
        }
        .tax {
          font-size: 16px;
        }
      }
    }
    .currencySymbol {
      font-size: 30px;
    }
    .totalPrice {
      font-size: 30px;
    }
    .currency {
      font-size: 18px;
    }
    .tax {
      font-size: 16px;
    }
    .label {
      font-size: 14px;
      padding: 3px 11px;
    }
  }
  ${ui.MQ_XS_SM} {
    padding: 22px 30px 0;

    .productName {
      font-size: 16px;
      margin-bottom: 1px;
    }
    .type {
      font-size: 14px;
      // margin-bottom: 6px;
    }
    .pdtPrice {
      font-size: 13px;
      &.member {
        margin-top: 10px;
        .currencySymbol,
        .totalPrice,
        .tax {
          font-size: 13px;
        }
      }
    }
    .currencySymbol {
      font-size: 16px;
    }
    .totalPrice {
      font-size: 16px;
    }
    .currency {
      font-size: 13px;
    }
    .tax {
      font-size: 13px;
    }
    .label {
      font-size: 11px;
      padding: 2px 7px;
    }
    .offersForMembers br {
      display: none;
    }
    .offersForMembers span {
      margin-left: 0.5em;
    }
  }
`;
